/**
 * Type definitions for Roles Management
 */

import i18n from 'i18next';
import type { Feature } from '@/types/rbac';

export interface MenuCategory {
  id: string;
  name: string;
  icon?: string;
  features: Feature[];
}

/**
 * Categorize features based on route and code - matches sidebar menu
 */
export const categorizeFeatures = (features: Feature[]): MenuCategory[] => {
  const categories: MenuCategory[] = [
    { id: 'dashboard', name: i18n.t('copy.hooks.roleDashboard'), features: [] },
    { id: 'live_tracking', name: i18n.t('copy.hooks.roleLiveMap'), features: [] },
    { id: 'deque', name: 'Deque', features: [] },
    { id: 'work_orders', name: i18n.t('copy.hooks.roleWorkOrders'), features: [] },
    { id: 'booking', name: i18n.t('copy.hooks.roleBooking'), features: [] },
    { id: 'workshop', name: i18n.t('copy.hooks.roleWorkshop'), features: [] },
    { id: 'key_management', name: i18n.t('copy.hooks.roleKeys'), features: [] },
    { id: 'temperature', name: i18n.t('copy.hooks.roleTemperature'), features: [] },
    { id: 'reports', name: i18n.t('copy.hooks.roleReports'), features: [] },
    { id: 'analysis', name: i18n.t('copy.hooks.roleAnalysis'), features: [] },
    { id: 'crm', name: 'CRM', features: [] },
    { id: 'admin', name: i18n.t('copy.hooks.roleAdmin'), features: [] },
    { id: 'sysadmin', name: 'SysAdmin', features: [] },
    { id: 'mobile', name: i18n.t('copy.hooks.roleMobile'), features: [] },
  ];

  for (const feature of features) {
    const code = feature.code || '';
    const route = feature.route || '';

    // Intern SYS_ADMIN-yta, inte tilldelningsbar i rollhantering
    if (code === 'SYSADMIN_SECURITY') {
      continue;
    }

    // mobile_enabled (t.ex. BOOKING_CALENDAR → dqbooking) ska synas under Mobilapp
    // även när feature också kategoriseras under Booking/Administration m.m.
    const isMobileCategoryFeature =
      feature.mobile_enabled ||
      code === 'MOBILE_ACCESS' ||
      code === 'SUPPORT_CHAT' ||
      code === 'DRIVING_LOG' ||
      code === 'HITTA_FORDON' ||
      route.startsWith('/driving-log');

    if (isMobileCategoryFeature) {
      categories.find(c => c.id === 'mobile')?.features.push(feature);
    }

    if (code === 'DASHBOARD' || code.startsWith('WIDGET_')) {
      categories.find(c => c.id === 'dashboard')?.features.push(feature);
    }
    else if (code === 'LIVE_TRACKING') {
      categories.find(c => c.id === 'live_tracking')?.features.push(feature);
    }
    else if (
      route.includes('/deque') ||
      // Deque: Flotta, Organisation, Tilldelningar, Bränslehantering, Service, Tidbok
      code === 'FLEET' ||
      code === 'ORGANIZATION' ||
      code === 'ASSIGNMENTS' ||
      code === 'FUEL' ||
      code === 'SYSTEM_SERVICE' ||
      code === 'TIDBOK_DIGITAL' ||
      // Deque: Kartjänster (Track, Geofence, Heatmap)
      code === 'ROUTE_ANALYSIS' ||
      code === 'ADMIN_GEOFENCES' ||
      code === 'ADMIN_GEOFENCE_EVENTS' ||
      code === 'ANALYSIS_HEATMAP' ||
      code === 'REPORT_GEOFENCE_ZONES' ||
      // Deque: Enheter
      code === 'ADMIN_DEVICES' ||
      code === 'ADMIN_DEVICE_MANAGEMENT' ||
      code === 'ADMIN_DEVICE_COMMANDS' ||
      // Deque: Fordonsutrustning (flyttad från Booking, behåller dock BOOKING_*-prefix
      // i kod/route för att inte bryta befintliga rolltilldelningar)
      code === 'BOOKING_EQUIPMENT_TAGS'
    ) {
      categories.find(c => c.id === 'deque')?.features.push(feature);
    }
    else if (code === 'WORK_ORDERS' || code.startsWith('WORK_ORDER_') || route.includes('/work-orders')) {
      categories.find(c => c.id === 'work_orders')?.features.push(feature);
    }
    else if (
      code === 'REPORTS' ||
      code.startsWith('REPORT_') ||
      code.startsWith('REPORTS_') ||
      route.startsWith('/reports')
    ) {
      // Före Booking: /reports/booking-stats innehåller "/booking" men är en rapport.
      categories.find(c => c.id === 'reports')?.features.push(feature);
    }
    else if (code === 'BOOKING' || code.startsWith('BOOKING_') || route.includes('/booking')) {
      categories.find(c => c.id === 'booking')?.features.push(feature);
    }
    else if (code === 'WORKSHOP' || code.startsWith('WORKSHOP_') || route.includes('/workshop')) {
      categories.find(c => c.id === 'workshop')?.features.push(feature);
    }
    else if (code === 'KEY_MANAGEMENT' || code.startsWith('KEY_') || route.includes('/key-management')) {
      categories.find(c => c.id === 'key_management')?.features.push(feature);
    }
    else if (
      code === 'TEMPERATURE_MONITORING' ||
      code.startsWith('TEMPERATURE_') ||
      route.startsWith('/temperature-monitoring')
    ) {
      categories.find(c => c.id === 'temperature')?.features.push(feature);
    }
    else if (code.startsWith('ANALYSIS_') || code === 'AI_ANALYSIS' || code === 'ROUTE_ANALYSIS' || route.startsWith('/analysis')) {
      categories.find(c => c.id === 'analysis')?.features.push(feature);
    }
    else if (code === 'CRM' || code.startsWith('CRM_') || route.startsWith('/crm')) {
      categories.find(c => c.id === 'crm')?.features.push(feature);
    }
    else if (code.startsWith('SYSADMIN_') || route.includes('/sysadmin')) {
      categories.find(c => c.id === 'sysadmin')?.features.push(feature);
    }
    else if (code.startsWith('ADMIN_') || route.startsWith('/admin') || 
             code === 'STATISTICS' || code === 'PROFILE') {
      categories.find(c => c.id === 'admin')?.features.push(feature);
    }
    else if (code.startsWith('HOMECARE') || route.includes('/homecare')) {
      categories.find(c => c.id === 'work_orders')?.features.push(feature);
    }
    else {
      // Alla övriga features (nya moduler) hamnar under Administration så att de alltid kan ges till roller
      categories.find(c => c.id === 'admin')?.features.push(feature);
    }
  }

  return categories
    .filter(c => c.features.length > 0)
    .map(c => ({
      ...c,
      features: [...c.features].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'sv', { sensitivity: 'base' })
      ),
    }));
};
